import React, { useState, useMemo } from 'react';
import { Button } from "@/components/ui/button";
import { FileText, Loader2 } from 'lucide-react';
import type { ProductData } from '@/components/scan-qr/ConfirmationPage';
import type { MerchantTerms } from '@/service/returnService';

type PlanVariant = 'accidental-damage' | 'extended-warranty'; // | 'return-product';

interface PlanOption {
    label: string;
    price: number;
}

interface ProtectionPlanPageProps {
    variant: PlanVariant;
    productData: ProductData;
    merchantTerms?: MerchantTerms;
    onContinue: (selectedPlan?: PlanOption) => void;
    onGoBack: () => void;
}

const variantContent: Record<PlanVariant, {
    title: string;
    subtitle: string;
    coverageTitle: string;
    coverageItems: string[];
    whyTitle: string;
    whyItems: string[];
    planSectionTitle?: string;
    plans: PlanOption[];
}> = {
    'accidental-damage': {
        title: 'Protect your product from accidental damage',
        subtitle: 'Coverage details and eligibility depend on the selected plan and merchant policy.',
        coverageTitle: 'Coverage may include:',
        coverageItems: [
            'Replacement for loss of diamond or gemstone.',
            'Resetting and repair of chipped or cracked stones.',
            'Refinishing, polishing and rhodium plating.',
        ],
        whyTitle: 'Why Add Accidental Care?',
        whyItems: [
            'Helps reduce repair or replacement costs',
            'Easy claim tracking via Protega dashboard',
            'Support handled as per merchant policy',
        ],
        planSectionTitle: 'Choose a protection Care plan',
        plans: [
            { label: '1 Year Protection', price: 800 },
            { label: '2 Year Protection', price: 1280 },
        ],
    },
    'extended-warranty': {
        title: "Extend your product's warranty",
        subtitle: 'Continue coverage for your product after the standard warranty period ends.',
        coverageTitle: 'Coverage may include:',
        coverageItems: [
            'Manufacturing or workmanship defects',
            'Functional issues covered under standard warranty',
            'Repairs as per extended warranty terms',
        ],
        whyTitle: 'Why Add Extended Warranty?',
        whyItems: [
            'Extends protection beyond the original warranty',
            'Helps manage repair costs over time',
            'Easy service support through Protega dashboard',
            'Extends protection beyond the original warranty',
            'Helps manage repair costs over time',
            'Easy service support through Protega dashboard',
        ],
        planSectionTitle: 'Choose a warranty plan',
        plans: [
            { label: '1 Year Extended Warranty', price: 800 },
            { label: '2 Year Extended Warranty', price: 1280 },
        ],
    },
    /*
    'return-product': {
        title: 'Start your return',
        subtitle: 'Please review the return policy before proceeding with your request.',
        coverageTitle: 'Return Policy Highlights:',
        coverageItems: [
            'Easy returns within 30 days of delivery',
            'Product must be in original condition with tags',
            'Refund issued to original payment method',
        ],
        whyTitle: 'Why use Protega Returns?',
        whyItems: [
            'Hassle-free pickup from your doorstep',
            'Real-time tracking of return status',
            'Quick Refund processing',
        ],
        plans: [], // No plans for returns
    },
    */
};

const ProtectionPlanPage: React.FC<ProtectionPlanPageProps> = ({
    variant,
    productData,
    merchantTerms,
    onContinue,
    onGoBack,
}) => {
    const baseContent = variantContent[variant];

    // Override coverageItems with dynamic terms from the API when available
    const content = useMemo(() => {
        if (!merchantTerms) return baseContent;

        if (variant === 'accidental-damage' && merchantTerms['Accidental Damage']) {
            return { ...baseContent, coverageItems: merchantTerms['Accidental Damage'] };
        }
        if (variant === 'extended-warranty' && merchantTerms['Extended Warranty']) {
            return { ...baseContent, coverageItems: merchantTerms['Extended Warranty'] };
        }
        return baseContent;
    }, [baseContent, merchantTerms, variant]);

    const [selectedPlanIndex, setSelectedPlanIndex] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleContinueSubmit = async () => {
        setIsSubmitting(true);
        await new Promise(resolve => setTimeout(resolve, 500));
        onContinue(content.plans.length > 0 ? content.plans[selectedPlanIndex] : undefined);
    };

    // Generate a preview URL for the product photo
    const photoPreviewUrl = useMemo(() => {
        if (productData.productPhoto) {
            return URL.createObjectURL(productData.productPhoto);
        }
        return null;
    }, [productData.productPhoto]);

    return (
        <div className="tw-h-full tw-flex tw-flex-col">
            <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-5 sm:tw-px-4">

                {/* Header */}
                <div className="tw-text-center tw-mb-1">
                    <h1 className="tw-text-xl sm:tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2 tw-leading-snug">
                        {content.title}
                    </h1>
                    <p className="tw-text-gray-500 tw-text-sm sm:tw-text-base">
                        {content.subtitle}
                    </p>
                </div>

                {/* Product Card */}
                <div className="tw-bg-gray-50 tw-rounded-2xl tw-overflow-hidden">
                    <div className="tw-flex tw-gap-4 tw-p-4">
                        {/* Product Photo */}
                        {photoPreviewUrl ? (
                            <div className="tw-w-28 tw-h-28 tw-rounded-xl tw-overflow-hidden tw-flex-shrink-0">
                                <img
                                    src={photoPreviewUrl}
                                    alt={productData.productName}
                                    className="tw-w-full tw-h-full tw-object-cover"
                                />
                            </div>
                        ) : (
                            <div className="tw-w-28 tw-h-28 tw-rounded-xl tw-bg-gray-200 tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center">
                                <span className="tw-text-gray-400 tw-text-xs">No photo</span>
                            </div>
                        )}

                        {/* Product Info */}
                        <div className="tw-flex tw-flex-col tw-justify-center tw-gap-1.5 tw-min-w-0">
                            <h3 className="tw-font-semibold tw-text-gray-900 tw-text-base tw-leading-tight tw-line-clamp-4">
                                {productData.productName || 'Unnamed Product'}
                            </h3>
                            <p className="tw-text-sm tw-text-gray-600">
                                Order ID: <span className="tw-font-medium">{productData.orderId}</span>
                            </p>
                        </div>
                    </div>

                    {productData.purchaseInvoice && (
                        <div className="tw-px-4 tw-pb-4">
                            <div className="tw-flex tw-items-center tw-gap-1.5 tw-bg-white tw-bg-opacity-50 tw-p-2 tw-rounded-lg tw-border tw-border-gray-200/50">
                                <FileText className="tw-w-4 tw-h-4 tw-text-gray-500 tw-flex-shrink-0" strokeWidth={1.5} />
                                <span className="tw-text-sm tw-text-gray-600 tw-truncate tw-flex-1">
                                    {productData.purchaseInvoice.name}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (productData.purchaseInvoice) {
                                            const url = URL.createObjectURL(productData.purchaseInvoice);
                                            window.open(url, '_blank');
                                        }
                                    }}
                                    className="tw-text-cyan-500 tw-text-sm tw-font-medium tw-bg-transparent tw-border-none tw-cursor-pointer tw-outline-none hover:tw-underline tw-flex-shrink-0"
                                >
                                    view
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Coverage section */}
                <div>
                    <h3 className="tw-font-semibold tw-text-gray-900 tw-text-sm tw-mb-2">
                        {content.coverageTitle}
                    </h3>
                    <ul className="tw-space-y-1.5 tw-text-sm tw-text-gray-700">
                        {content.coverageItems.map((item, i) => (
                            <li key={i} className="tw-flex tw-gap-2">
                                <span className="tw-text-gray-400 tw-flex-shrink-0">•</span>
                                <span>{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Why section */}
                <div>
                    <h3 className="tw-font-semibold tw-text-gray-900 tw-text-sm tw-mb-2">
                        {content.whyTitle}
                    </h3>
                    <ul className="tw-space-y-1.5 tw-text-sm tw-text-gray-700">
                        {content.whyItems.map((item, i) => (
                            <li key={i} className="tw-flex tw-gap-2">
                                <span className="tw-text-gray-400 tw-flex-shrink-0">•</span>
                                <span>{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>


                {/* Plan selection */}
                {content.plans.length > 0 && (
                    <div>
                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
                            <h3 className="tw-font-semibold tw-text-gray-900 tw-text-sm">
                                {content.planSectionTitle}
                            </h3>
                            <span className="tw-text-base">☺</span>
                        </div>
                        <p className="tw-text-[11px] tw-text-gray-400 tw-mb-3">
                            (Free Annual Doorstep Maintenance Service Included)
                        </p>

                        <div className="tw-flex tw-gap-3">
                            {content.plans.map((plan, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => setSelectedPlanIndex(i)}
                                    className={`tw-flex-1 tw-py-3 tw-px-3 tw-rounded-xl tw-text-center tw-cursor-pointer tw-outline-none tw-transition-all tw-border-2
                                        ${selectedPlanIndex === i
                                            ? 'tw-bg-cyan-400 tw-border-cyan-400 tw-text-white'
                                            : 'tw-bg-white tw-border-gray-200 tw-text-gray-900 hover:tw-border-gray-300'
                                        }`}
                                >
                                    <div className={`tw-font-semibold tw-text-xs tw-mb-0.5 ${selectedPlanIndex === i ? 'tw-text-white' : 'tw-text-gray-900'}`}>
                                        {plan.label}
                                    </div>
                                    <div className={`tw-font-bold tw-text-base ${selectedPlanIndex === i ? 'tw-text-white' : 'tw-text-gray-900'}`}>
                                        ₹{plan.price}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Action buttons */}
                <div className="tw-mt-auto tw-pb-4 sm:tw-pb-0 tw-flex tw-flex-col tw-items-center tw-gap-3 tw-pt-4">
                    <Button
                        onClick={handleContinueSubmit}
                        disabled={isSubmitting}
                        className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-h-12 tw-bg-cyan-50 hover:tw-bg-cyan-100 tw-text-cyan-600 disabled:tw-text-gray-400 disabled:tw-bg-gray-100 tw-font-semibold tw-text-lg tw-rounded-lg tw-border-none tw-transition-colors"
                    >
                        {isSubmitting && <Loader2 className="tw-w-5 tw-h-5 tw-animate-spin" />}
                        Continue
                    </Button>
                    <button
                        type="button"
                        onClick={onGoBack}
                        className="tw-text-gray-700 tw-font-medium tw-text-sm tw-bg-transparent tw-border-none tw-cursor-pointer tw-outline-none tw-underline tw-underline-offset-2 hover:tw-text-gray-900 tw-pb-2"
                    >
                        Go back
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProtectionPlanPage;
